import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  ClientApi,
  RentalOwnershipHeader,
  RentalOwnershipRecord,
} from "../../API/ClientApi";

const PAGE_SIZE = 10;

const searchFieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
} as const;

const midnight = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const apiDate = (date: Date | null) => {
  if (!date) return null;
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const displayDate = (date: Date | null) => {
  if (!date) return "Select date";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
};

const valueFor = (record: RentalOwnershipRecord, key: string): unknown =>
  record[key as keyof RentalOwnershipRecord];

const ScooterOwner: React.FC = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [headers, setHeaders] = useState<RentalOwnershipHeader[]>([]);
  const [records, setRecords] = useState<RentalOwnershipRecord[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [actionUserId, setActionUserId] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await new ClientApi().fetchRentalOwnershipDashboard(
        debouncedSearch,
        apiDate(fromDate),
        apiDate(toDate),
        page + 1,
        PAGE_SIZE
      );
      if (response.code !== 200 && response.code !== 2000) {
        throw new Error(response.message || "Unable to load scooter report.");
      }
      setHeaders(response.data.headers || []);
      setRecords(response.data.records || []);
      setTotalPages(response.data.pagination?.totalPages || 0);
    } catch (fetchError) {
      console.error("Error fetching rental ownership report:", fetchError);
      setError(true);
      setMessage("Unable to load scooter report. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, fromDate, toDate, page]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setFromDate(start ? midnight(start) : null);
    setToDate(end ? midnight(end) : null);
    setPage(0);
  };

  const handleAction = async (record: RentalOwnershipRecord, type: "ACCEPT" | "REJECT") => {
    setActionUserId(record.userId);
    try {
      const response = await new ClientApi().updateRentalOwnership(record.userId, type);
      if (response.code !== 200 && response.code !== 2000) {
        setError(true);
        setMessage(response.message || "Unable to update this scooter owner. Please try again.");
        return;
      }
      setError(false);
      setMessage(response.message || `${type === "ACCEPT" ? "Accepted" : "Rejected"} successfully`);
      await fetchReport();
    } catch (actionError) {
      console.error("Error updating rental ownership:", actionError);
      setError(true);
      setMessage("Unable to update this scooter owner. Please try again.");
    } finally {
      setActionUserId(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(false);
    try {
      const blob = await new ClientApi().exportRentalOwnership();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "rental-ownership-report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage("Report exported successfully");
    } catch (exportError) {
      console.error("Error exporting rental ownership report:", exportError);
      setError(true);
      setMessage("Unable to export scooter report. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const actionPillClass = (key: string) => {
    const base =
      "!min-w-[88px] !h-9 !rounded-full !px-4 !text-[13px] !font-bold !normal-case !text-white !shadow-none";
    if (key === "DONE") return `${base} !bg-slate-300`;
    if (key === "ACCEPT") return `${base} !bg-emerald-500 hover:!bg-emerald-600`;
    return `${base} !bg-rose-400 hover:!bg-rose-500`;
  };

  const renderCell = (record: RentalOwnershipRecord, header: RentalOwnershipHeader) => {
    if (header.type === "action") {
      return (
        <Box className="flex items-center gap-2.5">
          {record.actions?.map((action) => {
            const disabled = actionUserId === record.userId || action.key === "DONE";
            return (
              <Button
                key={action.key}
                disableRipple
                disabled={disabled}
                className={`${actionPillClass(action.key)} ${disabled ? "opacity-70" : ""}`}
                onClick={() => handleAction(record, action.key as "ACCEPT" | "REJECT")}
              >
                {actionUserId === record.userId ? (
                  <CircularProgress size={16} className="text-white" />
                ) : (
                  action.label
                )}
              </Button>
            );
          })}
        </Box>
      );
    }
    const value = valueFor(record, header.key);
    if (header.type === "amount")
      return <span className="text-base font-bold text-emerald-500">₹ {Number(value || 0).toFixed(0)}</span>;
    if (header.type === "datetime") return String(value || "-");
    return String(value ?? "-");
  };

  const columnCount = Math.max(headers.length, 1);

  return (
    <Box className="flex min-h-[calc(100vh-64px)] flex-col bg-slate-100 px-3 pb-6 pt-4 font-[Rubik,sans-serif] text-slate-900 sm:px-6">
      <Box className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <Box className="flex items-center gap-2">
          <Button
            aria-label="Back"
            disableRipple
            onClick={() => navigate("/scooter-owner")}
            className="min-w-0 rounded-full p-2 text-slate-700 hover:bg-slate-200/70"
          >
            <ArrowBackIcon />
          </Button>
          <Typography component="h1" className="!m-0 !text-[1.75rem] !font-bold !leading-tight tracking-tight">
            Manage Scooters
          </Typography>
        </Box>
        <Box className="flex items-center gap-2 text-[15px] font-medium text-slate-500">
          <HomeOutlinedIcon className="!text-[1.25rem] text-slate-400" />
          <span>/</span>
          <span className="text-orange-500">Dashboard</span>
          <span>/</span>
          <span className="text-slate-900">Manage Scooters</span>
        </Box>
      </Box>

      <Box className="flex min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white px-5 py-6 shadow-[0_8px_28px_rgba(15,23,42,0.06)] sm:px-7">
        <Box className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <Typography component="h2" className="!m-0 !text-[1.375rem] !font-bold">
            Scooter Report
          </Typography>
          <Box className="flex flex-wrap items-center gap-3">
            <DatePicker
              selectsRange
              startDate={fromDate}
              endDate={toDate}
              onChange={handleDateChange}
              dateFormat="dd-MM-yyyy"
              customInput={
                <button
                  type="button"
                  className="flex min-w-[340px] items-center gap-2.5 rounded-[10px] border border-indigo-200 bg-white px-4 py-3 text-[15px] font-semibold text-slate-700 transition-shadow hover:border-indigo-400 hover:shadow-[0_0_0_3px_rgba(99,102,241,0.08)]"
                >
                  <span>From : {displayDate(fromDate)}</span>
                  <span className="text-slate-300">|</span>
                  <span>To : {displayDate(toDate)}</span>
                  <KeyboardArrowDownIcon className="ml-auto text-slate-400" />
                </button>
              }
            />
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              className="min-w-[280px]"
              sx={searchFieldSx}
              InputProps={{
                className: "bg-slate-100 text-[15px]",
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon className="text-slate-400" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              disableRipple
              disabled={exporting}
              onClick={handleExport}
              startIcon={exporting ? <CircularProgress size={17} className="text-white" /> : <DownloadOutlinedIcon />}
              className="!h-[40px] !rounded-[10px] !border !border-emerald-500 !bg-emerald-500 !px-5 !text-[15px] !font-bold !normal-case !tracking-wide !text-white !shadow-[0_6px_14px_rgba(16,185,129,0.24)] !transition-all hover:!border-emerald-600 hover:!bg-emerald-600 hover:!shadow-[0_8px_18px_rgba(16,185,129,0.32)] focus:!shadow-[0_0_0_4px_rgba(16,185,129,0.18)] disabled:!border-emerald-300 disabled:!bg-emerald-300"
            >
              Export
            </Button>
          </Box>
        </Box>

        <Box className="flex-1 overflow-auto rounded-xl border border-slate-200">
          <Table className="min-w-[1100px]">
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell
                    key={header.key}
                    className="!whitespace-nowrap !border-b !border-slate-200 !bg-slate-50 !px-[18px] !py-4 !text-sm !font-bold !text-slate-600"
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" className="!h-72 !border-0">
                    <Box className="flex flex-col items-center gap-2.5 text-slate-400">
                      <CircularProgress size={32} className="text-emerald-500" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" className="!h-72 !border-0 !text-[15px] !text-slate-400">
                    Unable to load report
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" className="!h-72 !border-0">
                    <Box className="flex flex-col items-center gap-2.5 text-[15px] font-medium text-slate-400">
                      <InboxOutlinedIcon className="!text-5xl text-emerald-500" />
                      <span>No data found</span>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record.userId} className="hover:bg-slate-50">
                    {headers.map((header) => (
                      <TableCell
                        key={header.key}
                        className="!whitespace-nowrap !border-b !border-slate-100 !px-[18px] !py-[18px] !text-[15px] !font-medium !text-slate-700"
                      >
                        {renderCell(record, header)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>

        <Box className="flex items-center justify-end gap-2 pt-[18px] text-[15px] font-semibold text-slate-700">
          <Button
            aria-label="Previous page"
            disableRipple
            disabled={page === 0}
            onClick={() => setPage((current) => current - 1)}
            className="min-w-0 rounded-full p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            <KeyboardArrowLeftIcon fontSize="large" />
          </Button>
          <Typography className="!text-[15px] !font-semibold">{page + 1}</Typography>
          <Typography className="!text-[15px] !font-semibold">of {totalPages || 1}</Typography>
          <Button
            aria-label="Next page"
            disableRipple
            disabled={totalPages === 0 || page >= totalPages - 1}
            onClick={() => setPage((current) => current + 1)}
            className="min-w-0 rounded-full p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            <KeyboardArrowRightIcon fontSize="large" />
          </Button>
        </Box>
      </Box>

      <Typography className="pt-5 text-center !text-[13px] !font-semibold text-slate-700">
        © 2023 Zypp Electric. All rights reserved.
      </Typography>

      <Snackbar open={Boolean(message)} autoHideDuration={4000} onClose={() => setMessage("")}>
        <Alert severity={error ? "error" : "success"} onClose={() => setMessage("")}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScooterOwner;